import React from 'react';
import { X } from 'lucide-react';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useAppDispatch } from '../../store/hooks';
import { removeWidget, Widget } from '../../store/dashboardSlice';
import './WidgetCard.css';

interface WidgetCardProps {
  widget: Widget;
}

const COLORS = ['#3B4A9A', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];

const WidgetCard: React.FC<WidgetCardProps> = ({ widget }) => {
  const dispatch = useAppDispatch();

  const handleRemoveWidget = () => {
    dispatch(removeWidget(widget.id));
  };

  const renderChart = () => {
    if (!widget.chartData || !widget.chartData.labels.length) {
      return (
        <div className="chart-placeholder">
          <div className="chart-icon">📊</div>
          <p>No data available</p>
        </div>
      );
    }

    const data = widget.chartData.labels.map((label, index) => ({
      name: label,
      value: widget.chartData!.values[index]
    }));

    switch (widget.type) {
      case 'pie':
        const totalValue = data.reduce((sum, item) => sum + item.value, 0);
        
        const renderCenterContent = () => {
          const centerX = 90; // Half of 180px width
          const centerY = 90; // Half of 180px height
          
          return (
            <g>
              <text 
                x={centerX} 
                y={centerY - 5} 
                textAnchor="middle" 
                dominantBaseline="middle" 
                fontSize={20} 
                fontWeight="bold" 
                fill="#374151"
              >
                {totalValue}
              </text>
              <text 
                x={centerX} 
                y={centerY + 12} 
                textAnchor="middle" 
                dominantBaseline="middle" 
                fontSize={11} 
                fill="#6B7280"
                fontWeight="500"
              >
                TOTAL
              </text>
            </g>
          );
        };

        return (
          <div className="pie-chart-container">
            <div className="pie-chart-content">
              <div className="pie-chart-wrapper">
                <ResponsiveContainer width={180} height={180}>
                  <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                    <Pie
                      data={data}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={1}
                      dataKey="value"
                    >
                      {data.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: any) => [value, 'Count']} />
                    {renderCenterContent()}
                  </PieChart>
                </ResponsiveContainer>
              </div>
              
              <div className="pie-legend-right">
                {data.map((entry, index) => (
                  <div key={`legend-${index}`} className="legend-item-right">
                    <div 
                      className="legend-color-right" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    ></div>
                    <div className="legend-text-right">
                      <span className="legend-label-right">{entry.name}</span>
                      <span className="legend-value-right">{entry.value}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke={COLORS[0]} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        const progressTotal = data.reduce((sum, item) => sum + item.value, 0);
        let cumulativePercentage = 0;
        
        return (
          <div className="unified-progress-container">
            <div className="unified-progress-track">
              {data.map((item, index) => {
                const percentage = (item.value / progressTotal) * 100;
                const segmentStyle = {
                  left: `${cumulativePercentage}%`,
                  width: `${percentage}%`,
                  backgroundColor: COLORS[index % COLORS.length]
                };
                cumulativePercentage += percentage;
                
                return (
                  <div 
                    key={`segment-${index}`}
                    className="progress-segment"
                    style={segmentStyle}
                    title={`${item.name}: ${item.value}`}
                  />
                );
              })}
            </div>
            
            <div className="progress-legend">
              {data.map((item, index) => (
                <div key={`legend-${index}`} className="progress-legend-item">
                  <div 
                    className="progress-legend-color" 
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="progress-legend-text">
                    {item.name}: <strong>{item.value}</strong>
                  </span>
                </div>
              ))}
            </div>
            
            <div className="progress-total">
              Total: <strong>{progressTotal}</strong>
            </div>
          </div>
        );
      
      default:
        return (
          <div className="text-content">
            <p>{widget.text}</p>
          </div>
        );
    }
  };

  return (
    <div className={`widget-card widget-${widget.type}`}>
      <div className="widget-header">
        <div className="widget-title-section">
          <h3 className="widget-title">{widget.name}</h3>
          <span className="widget-type-badge">{widget.type === 'bar' ? 'PROGRESS' : widget.type.toUpperCase()}</span>
        </div>
        <button
          onClick={handleRemoveWidget}
          className="remove-widget-button"
          title="Remove widget"
        >
          <X size={16} />
        </button>
      </div>
      
      <div className="widget-content">
        {widget.text && <p className="widget-description">{widget.text}</p>}
        <div className="chart-container">
          {renderChart()}
        </div>
      </div>
    </div>
  );
};

export default WidgetCard;
